import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Crown, Sparkles, ArrowUpRight } from 'lucide-react';

const plans = [
  {
    n: '01',
    name: 'Pre-submission pilot',
    price: 'Scoped',
    desc: 'Time-bound pilot for one or more upcoming ESG/Climate filings.',
    features: [
      'Access to ESGine® for pilot window',
      'ESG-as-Code® checks in scope',
      'Interpretation support',
      'PDF & CSV exports',
    ],
  },
  {
    n: '02',
    name: 'Annual access',
    price: 'Recurring',
    desc: 'Ongoing access aligned to your reporting calendar.',
    features: [
      'Unlimited compliance checks',
      'Seven frameworks, 90+ rules',
      'Audit-ready exports',
      'Quarterly reviews with the ESGine® team',
      'Priority support',
    ],
    featured: true,
  },
  {
    n: '03',
    name: 'Embedded & integration',
    price: 'Enterprise',
    desc: 'Embedded into internal controls, reporting tools, or assurance workflows.',
    features: [
      'Dedicated rule libraries',
      'API & SSO access',
      'Governance & integration support',
      'Custom SLAs',
    ],
  },
];

export default function MyPlan() {
  return (
    <div className="max-w-[1280px]">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">
            Engagement
          </div>
          <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">
            My <span className="italic">plan.</span>
          </h2>
          <p className="mt-3 text-[14.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[640px]">
            Your current engagement and available models. Every engagement is scoped to filing complexity, applicable
            frameworks, and internal teams.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8F3EE] border border-[#1B6F5E]/25 text-[12px] text-[#1B6F5E]">
          <Crown className="w-3.5 h-3.5" />
          <span className="font-medium">Current plan: Annual access</span>
        </div>
      </div>

      {/* Current plan banner */}
      <div className="relative overflow-hidden rounded-2xl border border-[#0B1F2D] bg-[#0B1F2D] text-[#FAFAF5] p-7 lg:p-8 mb-8">
        <div className="absolute inset-0 grain opacity-25" />
        <div className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full bg-[#1B6F5E]/20 blur-3xl" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-[11px] tracking-[0.12em] uppercase text-[#7BC2B0]">
              <Sparkles className="w-3 h-3" /> Active engagement
            </div>
            <h3 className="mt-5 font-display text-[32px] leading-[1.05]">Annual access</h3>
            <p className="mt-3 text-[14px] text-[#FAFAF5]/70 max-w-[460px]">
              Ongoing access aligned to your reporting calendar, covering multiple filings across ESG, finance, legal,
              risk, and internal audit teams.
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3 self-center">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#FAFAF5]/55">Period</div>
              <div className="mt-1 font-display text-[20px]">2026 – 2027</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#FAFAF5]/55">Frameworks</div>
              <div className="mt-1 font-display text-[20px]">All 7</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#FAFAF5]/55">Renews</div>
              <div className="mt-1 font-display text-[20px]">Apr 28, 2027</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#FAFAF5]/55">Seats</div>
              <div className="mt-1 font-display text-[20px]">5 of 10</div>
            </div>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => (
          <div
            key={p.n}
            className={`rounded-2xl border p-7 flex flex-col card-lift ${
              p.featured
                ? 'bg-[#FAFAF5] border-[#0B1F2D] ring-1 ring-[#0B1F2D]/10'
                : 'bg-white border-[#E8E5DC]'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="font-mono-tight text-[11px] text-[#0B1F2D]/55">/{p.n}</span>
              {p.featured && (
                <span className="text-[10.5px] uppercase tracking-[0.12em] bg-[#0B1F2D] text-[#FAFAF5] px-2 py-1 rounded-full">
                  Current
                </span>
              )}
            </div>
            <div className="mt-7 font-display text-[24px] leading-[1.15] text-[#0B1F2D]">{p.name}</div>
            <div className="mt-1 text-[12.5px] text-[#0B1F2D]/55">{p.desc}</div>
            <div className="mt-5 font-display text-[28px] text-[#1B6F5E]">{p.price}</div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-[#0B1F2D]/80">
                  <Check className="w-3.5 h-3.5 text-[#1B6F5E] mt-1 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/#contact"
              className={`mt-7 inline-flex items-center justify-center gap-1.5 h-11 rounded-full text-[13px] font-medium transition-colors ${
                p.featured
                  ? 'bg-[#0B1F2D] text-[#FAFAF5] hover:bg-[#0B1F2D]/90'
                  : 'border border-[#0B1F2D]/20 text-[#0B1F2D] hover:bg-[#0B1F2D] hover:text-[#FAFAF5]'
              }`}
            >
              {p.featured ? 'Manage plan' : 'Request scoped proposal'} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
